import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAssessments } from "../../store/assessments/action";
import { useUserAuth } from "@workspace/login";
import DynamicTableWrapper from "../../components/DynamicTableWrapper/DynamicTableWrapper";
import ActionDropDown from "@workspace/common/src/Components/DynamicTable/Components/ActionDropDown";
import {
  fetchCandidates,
  fetchCandidatesFields,
  resetCandidates,
  fetchCandidateForm,
} from "@workspace/candidate/src/store/actions";
import {
  DynamicTableHelper,
  DateHelper,
  useTableHook,
} from "@workspace/common";
import {
  CANDIDATE_INITIAL_OPTIONS,
  CANDIDATE_MANDATORY_OPTIONS,
} from "@workspace/candidate/src/pages/CandidateListing/candidateListingConstants";
import {
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Offcanvas,
} from "reactstrap";

function ListOfAssessments() {
  document.title = "List of Assessments | Pulse";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { Permission, checkAllPermission } = useUserAuth();

  // States
  const [assignCandidateOffcanvas, setAssignCandidateOffcanvas] =
    useState(false);
  const [dropdownOpen, setDropdownOpen] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateFilterBubble, setCandidateFilterBubble] = useState(null);
  const [candidateCustomView, setCandidateCustomView] = useState(null);

  const toggleDropdown = (index) => {
    setDropdownOpen((prevState) => {
      const newState = [...prevState];
      newState[index] = !newState[index];
      return newState;
    });
  };

  // Selectors
  const allAssessments = useSelector(
    (state) => state.AssessmentsReducer.assessments
  );

  const candidatesData = useSelector(
    (state) => state.CandidateReducer.candidates
  );

  const candidatesFields = useSelector(
    (state) => state.CandidateReducer.candidatesFields
  );

  const allCandidateCustomViews = useSelector(
    (state) => state?.CandidateReducer?.candidateCustomViews
  );

  const candidateDefaultCustomView = useSelector(
    (state) => state?.CandidateReducer?.candidateDefaultCustomView
  );

  // Custom Render List
  const customRenderList = [
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
    {
      names: ["candidateSubmissionData.firstName"],
      render: (data) => (
        <div className="d-flex gap-2">
          <Link
            to={`/candidates/${data.id}/snapshot`}
            className="text-custom-primary text-decoration-underline"
          >
            {data.candidateSubmissionData.firstName}
          </Link>
          {data?.isDoNotDisturb && (
            <span
              title="Do not Disturb"
              className="mdi mdi-bell-off text-danger"
            >
              DnD
            </span>
          )}
        </div>
      ),
    },
  ];

  // Generate Candidate Config
  const generateCandidateConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: (
          <div className="form-check px-0">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkbox"
              checked={
                activeRow?.length > 0 && activeRow?.length === tableData?.length
              }
              onChange={(e) => selectAllRows(e?.target?.checked)}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        render: (data) => {
          return (
            <div className="form-check px-0">
              <Input
                className="form-check-input"
                type="checkbox"
                name="chk_child"
                checked={activeRow?.includes(parseInt(data?.id))}
                onChange={(e) => handleRowCheck(data?.id, e.target.checked)}
              />
            </div>
          );
        },
      },
      ...customConfig,
      {
        header: "Action",
        name: "action",
        sort: false,
        sortValue: "action",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => (
          <ActionDropDown>
            {checkAllPermission([Permission.CANDIDATE_EDIT]) && (
              <DropdownItem>
                <Link
                  to={`/candidates/${data.id}/snapshot`}
                  style={{ color: "black" }}
                  state={{ view: false }}
                >
                  <div className="d-flex  align-items-center gap-2">
                    <i className="mdi mdi-pencil"></i>
                    <span>Edit</span>
                  </div>
                </Link>
              </DropdownItem>
            )}
            {checkAllPermission([Permission.CANDIDATE_DELETE]) && (
              <DropdownItem>
                <span
                  type="button"
                  onClick={() => {
                    setDeleteId(data.id);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-delete"></i>
                    <span>Delete</span>
                  </div>
                </span>
              </DropdownItem>
            )}
            <DropdownItem
              onClick={() => {
                setCandidateTaggingOpen(!candidateTaggingOpen);
                setSelectedCandidate(data);
              }}
            >
              <span className="mdi mdi-account-plus me-2"></span>
              <span>Tag Candidate</span>
            </DropdownItem>
          </ActionDropDown>
        ),
      },
    ];
  };

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
      allActive: true,
    },
    CANDIDATE_MANDATORY_OPTIONS,
    CANDIDATE_INITIAL_OPTIONS,
    customRenderList,
    generateCandidateConfig
  );

  // Get All Option Groups
  useEffect(() => {
    dispatch(fetchCandidatesFields());
    dispatch(fetchCandidateForm("Candidate_basic_info"));
    return () => {
      dispatch(resetCandidates());
    };
  }, []);

  // Fetch the candidate when the pageRequest changes
  useEffect(() => {
    if (
      pageRequest?.searchFields?.length > 0 &&
      candidateFilterBubble != null
    ) {
      setActiveRow([]);
      dispatch(
        fetchCandidates(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            filters: candidateFilterBubble,
          })
        )
      );
    }
  }, [JSON.stringify(pageRequest), candidateFilterBubble]);

  // Update the page info when candidate Data changes
  useEffect(() => {
    if (candidatesData) {
      setPageInfoData(candidatesData);
      setTableData(candidatesData?.candidates);
    }
  }, [candidatesData]);

  // Update the custom config when the page info changes
  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateCandidateConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateCandidateConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  useEffect(() => {
    dispatch(fetchAssessments());
  }, []);

  const getCandidateBadgeText = (assessmentData) => {
    const candidateEmails = assessmentData.split(","); // Split string into an array
    if (assessmentData === "") {
      return `0`;
    } else if (candidateEmails.length <= 2) {
      return candidateEmails.join(", "); // Join the first 2 emails with a comma
    } else {
      const displayedEmails = candidateEmails.slice(0, 2).join(", "); // Get first 2 emails
      const remainingCount = candidateEmails.length - 2; // Calculate remaining candidates
      return (
        <div className="d-flex flex-row align-items-baseline gap-1">
          <span>{displayedEmails}</span>
          <span className="fw-semibold" style={{ color: "#3F88E4" }}>
            +{remainingCount}
          </span>
        </div>
      );
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row className="mb-3">
            <Col lg={12}>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="fw-semibold h5">Assessments</div>
                <div className="d-flex flex-row align-items-baseline gap-2">
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search.."
                      className="form-control"
                      style={{ width: "300px", borderColor: "#F7F8FC" }}
                    />
                    <span
                      className="search-icon mdi mdi-magnify"
                      style={{ color: "#3F88E4" }}
                    ></span>
                  </div>
                  <Button
                    className="d-flex flex-row align-items-baseline gap-1"
                    style={{
                      backgroundColor: "#3F88E4",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: "8px 26px",
                    }}
                    onClick={() => navigate("/assessment/create")}
                  >
                    <span className="mdi mdi-plus"></span>
                    <span>Create</span>
                  </Button>
                  <Button
                    className="d-flex flex-row align-items-baseline gap-1"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #C3D3E2",
                      borderRadius: "8px",
                      padding: "8px 26px",
                    }}
                  >
                    <span className="mdi mdi-filter-variant"></span>
                    <span>Filter</span>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              {allAssessments?.length > 0 &&
                allAssessments?.map((assessment, index) => {
                  return (
                    <Card
                      key={index}
                      style={{
                        backgroundColor: "#F9FCFF",
                        border: "1px solid #7AC7FF",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                    >
                      <CardBody>
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-baseline justify-content-between flex-row">
                            <div className="d-flex flex-row align-items-baseline gap-3">
                              <div style={{ fontSize: "18px" }}>
                                {assessment.assessmentName}
                              </div>
                              <div className="d-flex flex-row gap-1 align-items-center">
                                <span className="mdi mdi-calendar-blank-outline"></span>
                                <span>Dec 10, 2024 - 10:00 AM</span>
                              </div>
                            </div>
                            <Dropdown
                              isOpen={dropdownOpen[index] || false}
                              toggle={() => toggleDropdown(index)}
                            >
                              <DropdownToggle
                                data-toggle="dropdown"
                                tag="span"
                                className="mdi mdi-dots-horizontal cursor-pointer"
                              ></DropdownToggle>
                              <DropdownMenu>
                                <div
                                  onClick={() =>
                                    navigate(
                                      `/assessment/results/${assessment.id}`
                                    )
                                  }
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  View Results
                                </div>
                              </DropdownMenu>
                            </Dropdown>
                          </div>

                          <div
                            className="d-flex flex-row gap-2"
                            style={{ color: "#7F7F7F" }}
                          >
                            <span>1 Candidate Taking</span>
                            <span>|</span>
                            <span>10 Completed</span>
                            <span>|</span>
                            <span>Expire on {assessment.expiryDate}</span>
                          </div>

                          <div className="d-flex align-items-baseline justify-content-between flex-row">
                            <div className="d-flex flex-row align-items-baseline gap-2">
                              <div
                                className="d-flex flex-row align-items-baseline gap-1"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  borderColor: "#EAECF0",
                                  borderRadius: "3px",
                                  padding: "5px",
                                }}
                              >
                                <span
                                  className="mdi mdi-file-document-outline"
                                  style={{ color: "#3F88E4" }}
                                ></span>
                                <span
                                  style={{ color: "#3F88E4" }}
                                  className="fw-semibold"
                                >
                                  10
                                </span>
                                <span>Questions</span>
                              </div>
                              <div
                                className="d-flex flex-row align-items-baseline gap-1"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  borderColor: "#EAECF0",
                                  borderRadius: "3px",
                                  padding: "5px",
                                }}
                              >
                                <span
                                  className="mdi mdi-account-multiple-outline"
                                  style={{ color: "#3F88E4" }}
                                ></span>
                                <span>
                                  {getCandidateBadgeText(
                                    "suganth@gmail.com, jovyn@gmail.com, mary@gmail.com"
                                    // assessment.candidates
                                  )}
                                </span>
                              </div>
                              <div
                                className="d-flex flex-row align-items-baseline gap-1"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  borderColor: "#EAECF0",
                                  borderRadius: "3px",
                                  padding: "6px",
                                }}
                              >
                                <span
                                  className="mdi mdi-file-document-outline"
                                  style={{ color: "#3F88E4" }}
                                ></span>
                                <span
                                  className="fw-semibold"
                                  style={{ color: "#3F88E4" }}
                                >
                                  10
                                </span>
                                <span>Reports</span>
                              </div>
                              <div
                                className="d-flex flex-row align-items-baseline gap-1"
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  borderColor: "#EAECF0",
                                  borderRadius: "3px",
                                  padding: "5px",
                                }}
                              >
                                <span
                                  className="mdi mdi-timer-outline"
                                  style={{ color: "#3F88E4" }}
                                ></span>
                                <span
                                  style={{ color: "#3F88E4" }}
                                  className="fw-semibold"
                                >
                                  {assessment.durationHr}:
                                  {assessment.durationMin}
                                </span>
                                <span>min</span>
                              </div>
                            </div>
                            <div className="d-flex align-items-center flex-row gap-1">
                              <Button className="assessment-row-btns">
                                <span className="mdi mdi-cog-outline"></span>
                              </Button>
                              <Button className="assessment-row-btns">
                                <span className="mdi mdi-link"></span>
                              </Button>
                              <Button
                                className="assessment-row-btns"
                                onClick={() =>
                                  // handleShow(assessment.assessmentName)
                                  setAssignCandidateOffcanvas(true)
                                }
                              >
                                <span className="mdi mdi-account-plus-outline"></span>
                              </Button>
                              <Button className="assessment-row-btns">
                                <span className="mdi mdi-download-box-outline"></span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
            </Col>
          </Row>
          <Offcanvas
            isOpen={assignCandidateOffcanvas}
            toggle={() =>
              setAssignCandidateOffcanvas(!assignCandidateOffcanvas)
            }
            direction="end"
            backdrop={false}
            style={{ width: "75vw" }}
            scrollable={false}
          >
            <div
              className="offcanvas-header"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <div className="w-100 d-flex flex-row align-items-center justify-content-between">
                <h4 className="ps-2 fw-semibold modal-title">
                  Share Assessment with Candidates
                </h4>
                <span
                  className="mdi mdi-close cursor-pointer fs-4"
                  onClick={() => setAssignCandidateOffcanvas(false)}
                ></span>
              </div>
            </div>
            <div className="offcanvas-body px-3">
              <DynamicTableWrapper
                data={candidatesData.candidates}
                config={tableConfig}
                pageInfo={pageInfo}
                pageRequest={pageRequest}
                pageRequestSet={pageRequestSet}
                search={search}
                setSearch={setSearch}
                optGroup={candidatesFields}
                setCustomConfigData={setCustomConfigData}
                header="Candidates"
                activeRow={activeRow}
                setActiveRow={setActiveRow}
                setTableConfig={setTableConfig}
                candidateCustomView={candidateCustomView}
                setCandidateCustomView={setCandidateCustomView}
                candidateFilterBubble={candidateFilterBubble}
                setCandidateFilterBubble={setCandidateFilterBubble}
              />
            </div>
          </Offcanvas>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ListOfAssessments;
